/**
 * FikoSketch.ts — Parametrik 2D Sketch veri modeli + render (motor çekirdeği)
 *
 * Angular'dan bağımsız, salt THREE. İleride hem ayrı workspace'te hem de
 * mevcut viewer içinde "modelleme modu" olarak yeniden kullanılabilir.
 *
 * Sketch bir DÜZLEM üzerinde yaşar (origin + U/V eksen bazı). Elemanlar 2D
 * düzlem koordinatlarında tutulur; ciz() bunları dünya uzayına basıp tek bir
 * THREE.Group içine çizer. Constraint solver (3B) bu modelin üstüne oturacak.
 */

import * as THREE from 'three';
import { FikoKisitSolver, IKisit, KisitTip } from './FikoKisitSolver';

export type SketchTip = 'cizgi' | 'dikdortgen' | 'cember' | 'yay' | 'nokta' | 'cokgen' | 'slot' | 'elips' | 'spline' | 'konik';

export interface ISketchNokta { x: number; y: number; }

export interface ISketchEleman {
  id: number;
  tip: SketchTip;
  noktalar: ISketchNokta[]; // cizgi[2], dikdortgen[2 köşe], cember[merkez], yay[merkez,baş,son], cokgen[merkez,(ilk köşe)], slot[merkez1,merkez2], elips[merkez], spline[kontrol noktaları]
  r?: number;               // cember/yay/cokgen yarıçapı, slot genişlik/2, elips büyük yarıçap (a)
  r2?: number;              // elips küçük yarıçap (b)
  aci?: number;             // elips dönme açısı (rad)
  kapali?: boolean;         // spline kapalı mı (CATIA closed spline)
  kenarSayisi?: number;     // PARAMETRİK çokgen: kenar adedi (≥3) — sonradan düzenlenebilir
  yapi?: boolean;           // construction geometri (üretime girmez, kesik çizgi)
  konikTip?: 'parabol' | 'hiperbol';   // KONİK eleman alt türü (noktalar[0]=tepe/merkez, r=a, r2=b, aci=dönme)
  konikT0?: number; konikT1?: number;  // konik parametre aralığı
}

export type OlcuTip = 'uzunluk' | 'yaricap' | 'cap' | 'genislik' | 'yukseklik';

/** Boyutsal constraint (CATIA dimension): elemana bağlı, düzenlenebilir değer. */
export interface ISketchOlcu {
  id: number;
  tip: OlcuTip;
  elemanId: number;
  deger: number;
}

export interface ISketchDuzlem {
  origin: THREE.Vector3;
  normal: THREE.Vector3;
  eksenU: THREE.Vector3;
  eksenV: THREE.Vector3;
}

const RENK_CIZGI = 0x4a90d9;
const RENK_SECILI = 0xffa726;
const CEMBER_SEGMENT = 64;

export class FikoSketch {
  readonly nesne = new THREE.Group();
  duzlem: ISketchDuzlem;
  elemanlar: ISketchEleman[] = [];
  private sayac = 1;
  private seciliId: number | null = null;
  private hoverId: number | null = null;

  private malzeme = new THREE.LineBasicMaterial({ color: RENK_CIZGI });
  private malzemeSecili = new THREE.LineBasicMaterial({ color: RENK_SECILI, linewidth: 2 });
  private malzemeHover = new THREE.LineBasicMaterial({ color: 0x22d3ee, linewidth: 2 });
  private malzemeYapi = new THREE.LineDashedMaterial({ color: 0x8890a8, dashSize: 3, gapSize: 2 });
  private malzemeOlcu = new THREE.LineBasicMaterial({ color: 0xffca28, transparent: true, opacity: 0.92, depthTest: false });
  private malzemeOlcuSecili = new THREE.LineBasicMaterial({ color: 0xff7043, transparent: true, opacity: 1, depthTest: false });
  private malzemeKisitli = new THREE.LineBasicMaterial({ color: 0x34d399 });   // yatay/dikey kısıtlı çizgi (tam tanımlı)
  private malzemeGlyph = new THREE.LineBasicMaterial({ color: 0xf06292, transparent: true, opacity: 0.95, depthTest: false }); // kısıt simgesi (SW tarzı)
  /** Otomatik ölçülendirme: her eleman sürekli ölçü gösterir (CATIA sketcher gibi). */
  otomatikOlcu = true;
  /** Ölçü/kısıt görselleri SADECE bu sketch düzenlenirken çizilir (CATIA gibi). */
  olcuGoster = false;

  /** Kalıcı geometrik kısıt katmanı (iteratif solver). Tek-seferlik kısıtların ÜSTÜNE oturur. */
  readonly kisitSolver: FikoKisitSolver = new FikoKisitSolver(this);
  /** Kullanım kolaylığı: kalıcı kısıt dizisi (solver'ın sahip olduğu). */
  get kisitlar(): IKisit[] { return this.kisitSolver.kisitlar; }
  private malzemeKisitGlyph = new THREE.LineBasicMaterial({ color: 0xa855f7, transparent: true, opacity: 0.95, depthTest: false }); // kalıcı kısıt simgesi (mor)
  private malzemeTam = new THREE.LineBasicMaterial({ color: 0xa855f7 });    // iso-tanımlı (CATIA: mor/koyu yeşil)
  private malzemeAsiri = new THREE.LineBasicMaterial({ color: 0xef4444 });  // aşırı kısıtlı (kırmızı)

  constructor(duzlem?: Partial<ISketchDuzlem>) {
    this.nesne.name = 'FikoSketch';
    this.duzlem = this.duzlemNormalize(duzlem);
  }

  /** Kalıcı kısıt ekle (solver'a). tip + hedef eleman/uç + opsiyonel parametre. */
  kisitEkleKalici(tip: KisitTip, hedefler: { elemanId: number; uc?: number }[], parametre?: number): IKisit {
    return this.kisitSolver.ekle(tip, hedefler, parametre);
  }

  /** Kısıtları çöz (sürükleme sonrası). kilitler: sabit tutulacak değişken anahtarları. */
  kisitCoz(kilitler: string[] = []): boolean {
    const ok = this.kisitSolver.coz(kilitler);
    this.ciz();
    return ok;
  }

  // ---- düzlem ----

  /** XY/XZ/YZ ön tanımlı düzlemler. */
  static duzlemHazir(tip: 'xy' | 'xz' | 'yz', origin = new THREE.Vector3()): ISketchDuzlem {
    switch (tip) {
      case 'xz': return { origin: origin.clone(), normal: new THREE.Vector3(0, 1, 0), eksenU: new THREE.Vector3(1, 0, 0), eksenV: new THREE.Vector3(0, 0, -1) };
      case 'yz': return { origin: origin.clone(), normal: new THREE.Vector3(1, 0, 0), eksenU: new THREE.Vector3(0, 1, 0), eksenV: new THREE.Vector3(0, 0, -1) };
      case 'xy':
      default: return { origin: origin.clone(), normal: new THREE.Vector3(0, 0, 1), eksenU: new THREE.Vector3(1, 0, 0), eksenV: new THREE.Vector3(0, 1, 0) };
    }
  }

  private duzlemNormalize(d?: Partial<ISketchDuzlem>): ISketchDuzlem {
    if (d?.origin && d.normal && d.eksenU && d.eksenV) return d as ISketchDuzlem;
    return FikoSketch.duzlemHazir('xy');
  }

  /** 2D düzlem koordinatı → dünya uzayı. */
  duzlem2Dunya(p: ISketchNokta, hedef = new THREE.Vector3()): THREE.Vector3 {
    return hedef.copy(this.duzlem.origin)
      .addScaledVector(this.duzlem.eksenU, p.x)
      .addScaledVector(this.duzlem.eksenV, p.y);
  }

  /** Dünya uzayı noktası → 2D düzlem koordinatı (düzleme izdüşüm). */
  dunya2Duzlem(w: THREE.Vector3): ISketchNokta {
    const rel = w.clone().sub(this.duzlem.origin);
    return { x: rel.dot(this.duzlem.eksenU), y: rel.dot(this.duzlem.eksenV) };
  }

  // ---- eleman CRUD ----

  ekle(tip: SketchTip, noktalar: ISketchNokta[], r?: number): ISketchEleman {
    const el: ISketchEleman = { id: this.sayac++, tip, noktalar, r };
    this.elemanlar.push(el);
    this.ciz();
    return el;
  }

  sil(id: number): void {
    this.elemanlar = this.elemanlar.filter(e => e.id !== id);
    this.olculer = this.olculer.filter(o => o.elemanId !== id);  // o elemana bağlı ölçüleri de sil
    this.kisitSolver.elemanKisitlariSil(id);                     // o elemana bağlı kalıcı kısıtları da sil (cascade)
    if (this.seciliId === id) this.seciliId = null;
    if (this.hoverId === id) this.hoverId = null;
    this.ciz();
  }

  /** Construction (yapı) geometri aç/kapat — üretime girmez, kesik çizgi. */
  yapiToggle(id: number): void {
    const el = this.elemanlar.find(e => e.id === id);
    if (el) { el.yapi = !el.yapi; this.ciz(); }
  }

  temizle(): void {
    this.elemanlar = [];
    this.seciliId = null;
    this.ciz();
  }

  sec(id: number | null): void {
    this.seciliId = id;
    this.ciz();
  }

  /** Hover vurgusu (seçimden farklı renk — cyan). */
  hover(id: number | null): void {
    if (this.hoverId === id) return;
    this.hoverId = id;
    this.ciz();
  }

  // ---- boyutsal ölçüler (dimensions) ----
  olculer: ISketchOlcu[] = [];
  private olcuSayac = 1;

  /** Bir elemana ölçü ekler (tür otomatik): çizgi→uzunluk, çember→yarıçap. */
  olcuEkle(elemanId: number): ISketchOlcu | null {
    const el = this.elemanlar.find(e => e.id === elemanId);
    if (!el) return null;
    if (this.olculer.some(o => o.elemanId === elemanId)) return null; // zaten var
    let tip: OlcuTip; let deger: number;
    if (el.tip === 'cizgi') { tip = 'uzunluk'; deger = this.elemanDeger(el, 'uzunluk'); }
    else if (el.tip === 'cember' || el.tip === 'yay') { tip = 'yaricap'; deger = this.elemanDeger(el, 'yaricap'); }
    else if (el.tip === 'dikdortgen') { tip = 'genislik'; deger = Math.abs(el.noktalar[1].x - el.noktalar[0].x); }
    else return null;
    const o: ISketchOlcu = { id: this.olcuSayac++, tip, elemanId, deger };
    this.olculer.push(o);
    return o;
  }

  /** Yay/çemberin gerçek merkez+yarıçapı (yay için 3-nokta cevreCember). */
  cemberGeo(el: ISketchEleman): { merkez: ISketchNokta; r: number } | null {
    if (el.tip === 'cember' || el.tip === 'cokgen') return { merkez: el.noktalar[0], r: el.r || 0 };
    if (el.tip === 'yay') return FikoSketch.cevreCember(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
    return null;
  }

  /** Elemanın güncel geometrik değerini döndürür. */
  elemanDeger(el: ISketchEleman, tip: OlcuTip): number {
    if (tip === 'uzunluk') { const [a, b] = el.noktalar; return Math.hypot(b.x - a.x, b.y - a.y); }
    if (tip === 'yaricap') { const c = this.cemberGeo(el); return c ? c.r : 0; }
    if (tip === 'cap') { const c = this.cemberGeo(el); return c ? c.r * 2 : 0; }
    if (tip === 'genislik') return Math.abs(el.noktalar[1].x - el.noktalar[0].x);
    if (tip === 'yukseklik') return Math.abs(el.noktalar[1].y - el.noktalar[0].y);
    return 0;
  }

  /** Ölçü değerini değiştirir → geometriyi yeniden boyutlandırır (parametrik). */
  olcuUygula(olcuId: number, yeniDeger: number): boolean {
    const o = this.olculer.find(x => x.id === olcuId);
    if (!o || yeniDeger <= 0) return false;
    const el = this.elemanlar.find(e => e.id === o.elemanId);
    if (!el) return false;
    o.deger = yeniDeger;
    if (o.tip === 'uzunluk' && el.tip === 'cizgi') {
      const a = el.noktalar[0], b = el.noktalar[1];
      const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      el.noktalar[1] = { x: a.x + (b.x - a.x) / len * yeniDeger, y: a.y + (b.y - a.y) / len * yeniDeger };
    } else if ((o.tip === 'yaricap' || o.tip === 'cap') && el.tip === 'yay') {
      // Yay: merkez sabit, 3 noktayı yeni yarıçapa ölçekle
      const yeniR = o.tip === 'cap' ? yeniDeger / 2 : yeniDeger;
      const c = FikoSketch.cevreCember(el.noktalar[0], el.noktalar[1], el.noktalar[2]); if (!c) return false;
      const sc = yeniR / (c.r || 1);
      el.noktalar = el.noktalar.map(p => ({ x: c.merkez.x + (p.x - c.merkez.x) * sc, y: c.merkez.y + (p.y - c.merkez.y) * sc }));
    } else if (o.tip === 'yaricap') { el.r = yeniDeger; }
    else if (o.tip === 'cap') { el.r = yeniDeger / 2; }
    else if (o.tip === 'genislik' && el.tip === 'dikdortgen') {
      const a = el.noktalar[0], b = el.noktalar[1]; el.noktalar[1] = { x: a.x + Math.sign(b.x - a.x || 1) * yeniDeger, y: b.y };
    } else if (o.tip === 'yukseklik' && el.tip === 'dikdortgen') {
      const a = el.noktalar[0], b = el.noktalar[1]; el.noktalar[1] = { x: b.x, y: a.y + Math.sign(b.y - a.y || 1) * yeniDeger };
    }
    this.ciz();
    return true;
  }

  /** Ölçü etiketinin 2D düzlem konumu (anchor). */
  olcuAnchor(o: ISketchOlcu): ISketchNokta | null {
    const el = this.elemanlar.find(e => e.id === o.elemanId);
    if (!el) return null;
    if (el.tip === 'cizgi') { const [a, b] = el.noktalar; return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
    if (el.tip === 'cember' || el.tip === 'yay') { const c = this.cemberGeo(el); if (!c) return null; return { x: c.merkez.x, y: c.merkez.y + c.r }; }
    if (el.tip === 'dikdortgen') { const [a, b] = el.noktalar; return o.tip === 'yukseklik' ? { x: b.x, y: (a.y + b.y) / 2 } : { x: (a.x + b.x) / 2, y: a.y }; }
    return null;
  }

  olcuSil(id: number): void { this.olculer = this.olculer.filter(o => o.id !== id); }

  private static rot(d: ISketchNokta, a: number): ISketchNokta {
    const c = Math.cos(a), s = Math.sin(a);
    return { x: d.x * c - d.y * s, y: d.x * s + d.y * c };
  }

  /** CATIA-tarzı ölçü yerleşimi: uzatma çizgileri + ölçü çizgisi + ok yönleri + metin konumu (2D düzlem). */
  olcuYerlesim(o: ISketchOlcu): { segs: [ISketchNokta, ISketchNokta][]; oklar: { p: ISketchNokta; d: ISketchNokta }[]; metin: ISketchNokta } | null {
    const el = this.elemanlar.find(e => e.id === o.elemanId);
    if (!el) return null;
    const OFF = 14, GAP = 1.5, EXT = 3;
    const segs: [ISketchNokta, ISketchNokta][] = [];
    const oklar: { p: ISketchNokta; d: ISketchNokta }[] = [];

    if ((o.tip === 'uzunluk' && el.tip === 'cizgi') || ((o.tip === 'genislik' || o.tip === 'yukseklik') && el.tip === 'dikdortgen')) {
      let a: ISketchNokta, b: ISketchNokta, n: ISketchNokta;
      if (el.tip === 'cizgi') {
        a = el.noktalar[0]; b = el.noktalar[1];
        const L = Math.hypot(b.x - a.x, b.y - a.y) || 1; n = { x: -(b.y - a.y) / L, y: (b.x - a.x) / L };
      } else {
        const [c, d] = el.noktalar;
        const x0 = Math.min(c.x, d.x), x1 = Math.max(c.x, d.x), y0 = Math.min(c.y, d.y), y1 = Math.max(c.y, d.y);
        if (o.tip === 'genislik') { a = { x: x0, y: y0 }; b = { x: x1, y: y0 }; n = { x: 0, y: -1 }; }
        else { a = { x: x1, y: y0 }; b = { x: x1, y: y1 }; n = { x: 1, y: 0 }; }
      }
      const L = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const dir = { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
      const p1 = { x: a.x + n.x * OFF, y: a.y + n.y * OFF };
      const p2 = { x: b.x + n.x * OFF, y: b.y + n.y * OFF };
      segs.push([p1, p2]);
      segs.push([{ x: a.x + n.x * GAP, y: a.y + n.y * GAP }, { x: a.x + n.x * (OFF + EXT), y: a.y + n.y * (OFF + EXT) }]);
      segs.push([{ x: b.x + n.x * GAP, y: b.y + n.y * GAP }, { x: b.x + n.x * (OFF + EXT), y: b.y + n.y * (OFF + EXT) }]);
      oklar.push({ p: p1, d: dir }, { p: p2, d: { x: -dir.x, y: -dir.y } });
      return { segs, oklar, metin: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 } };
    }

    if (el.tip === 'cember' || el.tip === 'yay') {
      const cg = this.cemberGeo(el); if (!cg) return null;
      const m = cg.merkez; const r = cg.r; const dir = { x: 0.7071, y: 0.7071 };
      if (o.tip === 'cap') {
        const p1 = { x: m.x - dir.x * r, y: m.y - dir.y * r }, p2 = { x: m.x + dir.x * r, y: m.y + dir.y * r };
        segs.push([p1, p2]); oklar.push({ p: p1, d: { x: -dir.x, y: -dir.y } }, { p: p2, d: dir });
        return { segs, oklar, metin: { x: m.x + dir.x * r * 0.4, y: m.y + dir.y * r * 0.4 } };
      }
      const p2 = { x: m.x + dir.x * r, y: m.y + dir.y * r };
      segs.push([m, p2]); oklar.push({ p: p2, d: dir });
      return { segs, oklar, metin: { x: m.x + dir.x * r * 0.55, y: m.y + dir.y * r * 0.55 } };
    }
    return null;
  }

  /** Eksik ölçüleri otomatik üret + tüm ölçü değerlerini geometriyle senkronla (canlı). */
  private olculeriSenkron(): void {
    if (this.otomatikOlcu) {
      const ekle = (elemanId: number, tip: OlcuTip) => {
        if (this.olculer.some(o => o.elemanId === elemanId && o.tip === tip)) return;
        const el = this.elemanlar.find(e => e.id === elemanId); if (!el) return;
        this.olculer.push({ id: this.olcuSayac++, tip, elemanId, deger: this.elemanDeger(el, tip) });
      };
      for (const el of this.elemanlar) {
        if (el.yapi) continue;
        if (el.tip === 'cizgi') ekle(el.id, 'uzunluk');
        else if (el.tip === 'cember' || el.tip === 'yay') ekle(el.id, 'yaricap');
        else if (el.tip === 'dikdortgen') { ekle(el.id, 'genislik'); ekle(el.id, 'yukseklik'); }
      }
    }
    this.olculer = this.olculer.filter(o => { const el = this.elemanlar.find(e => e.id === o.elemanId); if (!el || el.yapi) return false; o.deger = this.elemanDeger(el, o.tip); return true; });
  }

  /** Ölçüleri 3D olarak sketch düzleminde çizer (uzatma + ölçü çizgisi + oklar). */
  private olculeriCiz(): void {
    if (!this.olculer.length) return;
    const pts: THREE.Vector3[] = [], ptsSec: THREE.Vector3[] = []; const OK_L = 3, OK_A = 0.32;
    for (const o of this.olculer) {
      const y = this.olcuYerlesim(o); if (!y) continue;
      const hedef = o.elemanId === this.seciliId ? ptsSec : pts;   // seçili elemanın ölçüsü vurgulu (Shapr3D)
      for (const [a, b] of y.segs) hedef.push(this.duzlem2Dunya(a), this.duzlem2Dunya(b));
      for (const ok of y.oklar) {
        const back = { x: -ok.d.x, y: -ok.d.y };
        const w1 = FikoSketch.rot(back, OK_A), w2 = FikoSketch.rot(back, -OK_A);
        hedef.push(this.duzlem2Dunya(ok.p), this.duzlem2Dunya({ x: ok.p.x + w1.x * OK_L, y: ok.p.y + w1.y * OK_L }));
        hedef.push(this.duzlem2Dunya(ok.p), this.duzlem2Dunya({ x: ok.p.x + w2.x * OK_L, y: ok.p.y + w2.y * OK_L }));
      }
    }
    if (pts.length) { const s = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), this.malzemeOlcu); s.userData['olcuNesne'] = true; s.renderOrder = 6; this.nesne.add(s); }
    if (ptsSec.length) { const s = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(ptsSec), this.malzemeOlcuSecili); s.userData['olcuNesne'] = true; s.renderOrder = 6; this.nesne.add(s); }
    // CATIA: ölçü METNİ de sketch düzleminde 3D (CanvasTexture, modelle döner)
    for (const o of this.olculer) { const y = this.olcuYerlesim(o); if (y) this.nesne.add(this.olcuMetinMesh(o, y.metin)); }
  }

  // ---- 3D ÖLÇÜ METNİ (CATIA: sketch düzlemine yapışık sayı etiketi, modelle döner) ----
  private olcuTexCache = new Map<string, { tex: THREE.Texture; mat: THREE.MeshBasicMaterial; aspect: number }>();
  private olcuMetinStr(o: ISketchOlcu): string {
    const onek = o.tip === 'yaricap' ? 'R ' : o.tip === 'cap' ? 'Ø ' : '';
    return onek + o.deger.toFixed(1);
  }
  private static yuvardikDik(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
  }
  private olcuTextureAl(metin: string, secili: boolean): { tex: THREE.Texture; mat: THREE.MeshBasicMaterial; aspect: number } {
    const anahtar = (secili ? 'S|' : '') + metin;
    const v = this.olcuTexCache.get(anahtar); if (v) return v;
    if (this.olcuTexCache.size > 64) { for (const x of this.olcuTexCache.values()) { x.tex.dispose(); x.mat.dispose(); } this.olcuTexCache.clear(); }   // drag birikmesi engeli
    const fs = 44, pad = 12;
    const olc = document.createElement('canvas').getContext('2d')!; olc.font = `bold ${fs}px Arial, sans-serif`;
    const tw = Math.ceil(olc.measureText(metin).width);
    const w = tw + pad * 2, h = fs + pad;
    const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    const c = cv.getContext('2d')!; c.font = `bold ${fs}px Arial, sans-serif`;
    c.fillStyle = secili ? 'rgba(60,20,5,0.88)' : 'rgba(16,24,38,0.86)';
    FikoSketch.yuvardikDik(c, 1.5, 1.5, w - 3, h - 3, 9); c.fill();
    c.strokeStyle = secili ? 'rgba(255,112,67,0.95)' : 'rgba(255,202,40,0.9)'; c.lineWidth = 2.5;
    FikoSketch.yuvardikDik(c, 1.5, 1.5, w - 3, h - 3, 9); c.stroke();
    c.fillStyle = secili ? '#ffab91' : '#ffd54a'; c.textBaseline = 'middle'; c.textAlign = 'center';
    c.fillText(metin, w / 2, h / 2 + 1);
    const tex = new THREE.CanvasTexture(cv); tex.anisotropy = 4; tex.minFilter = THREE.LinearFilter; tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
    const sonuc = { tex, mat, aspect: w / h };
    this.olcuTexCache.set(anahtar, sonuc);
    return sonuc;
  }
  private olcuMetinMesh(o: ISketchOlcu, metin2d: ISketchNokta): THREE.Mesh {
    const secili = o.elemanId === this.seciliId;
    const { mat, aspect } = this.olcuTextureAl(this.olcuMetinStr(o), secili);
    const H = 6, W = H * aspect;   // ~6mm dünya yüksekliği
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(W, H), mat);
    mesh.position.copy(this.duzlem2Dunya(metin2d));
    mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(this.duzlem.eksenU, this.duzlem.eksenV, this.duzlem.normal));
    mesh.renderOrder = 7; mesh.userData['olcuNesne'] = true; mesh.userData['olcuId'] = o.id;
    return mesh;
  }

  /** Çizginin geometrik kısıt durumu (yatay/dikey kısıtlı mı, yoksa serbest mi). */
  elemanKisit(el: ISketchEleman): 'yatay' | 'dikey' | 'serbest' | null {
    if (el.tip !== 'cizgi') return null;
    const [a, b] = el.noktalar;
    const dx = Math.abs(b.x - a.x), dy = Math.abs(b.y - a.y);
    if (dy < 1e-3 && dx > 1e-3) return 'yatay';
    if (dx < 1e-3 && dy > 1e-3) return 'dikey';
    return 'serbest';
  }

  /** Kısıt simgelerini (yatay→"=", dikey→"||") sketch düzleminde çizer (SolidWorks/CATIA tarzı). */
  private kisitlariCiz(): void {
    const pts: THREE.Vector3[] = [];
    for (const el of this.elemanlar) {
      if (el.yapi) continue;
      const k = this.elemanKisit(el); if (k !== 'yatay' && k !== 'dikey') continue;
      const [a, b] = el.noktalar; const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const L = Math.hypot(b.x - a.x, b.y - a.y) || 1; const dir = { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
      const n = { x: -dir.y, y: dir.x };
      const c = { x: mid.x + n.x * 4, y: mid.y + n.y * 4 };  // çizgiden offset'li simge merkezi
      for (const s of [0.75, -0.75]) {                        // çizgi yönünde iki kısa paralel tire
        const o = { x: c.x + n.x * s, y: c.y + n.y * s };
        pts.push(this.duzlem2Dunya({ x: o.x - dir.x * 1.4, y: o.y - dir.y * 1.4 }), this.duzlem2Dunya({ x: o.x + dir.x * 1.4, y: o.y + dir.y * 1.4 }));
      }
    }
    if (pts.length) {
      const seg = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), this.malzemeGlyph);
      seg.userData['kisitNesne'] = true; seg.renderOrder = 7;
      this.nesne.add(seg);
    }
    this.kaliciKisitlariCiz();   // solver'daki kalıcı kısıtların simgeleri (paralel/dik/teğet/eşit/coincidence...)
  }

  /** Kalıcı (solver) kısıtların glyph simgelerini sketch düzleminde çizer. */
  private kaliciKisitlariCiz(): void {
    const glyphler = this.kisitSolver.glyphler();
    if (!glyphler.length) return;
    const pts: THREE.Vector3[] = [];
    const SZ = 2.2;            // simge yarı-boyut (mm)
    const OFF = 6;             // elemandan dik kaçınma
    let i = 0;
    for (const g of glyphler) {
      const aci = g.aci ?? 0;
      const nx = -Math.sin(aci), ny = Math.cos(aci);          // çizgi normali
      const kademe = OFF + (i % 4) * (SZ * 2.4);              // üst üste binmeyi azalt
      const c = { x: g.p.x + nx * kademe, y: g.p.y + ny * kademe };
      this.glyphSimge(pts, g.tip, c, SZ, aci);
      i++;
    }
    if (!pts.length) return;
    const seg = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), this.malzemeKisitGlyph);
    seg.userData['kisitNesne'] = true; seg.renderOrder = 7;
    this.nesne.add(seg);
  }

  /** Tek bir kısıt simgesinin çizgi segmentlerini (2D düzlem) pts'e ekler. */
  private glyphSimge(pts: THREE.Vector3[], tip: KisitTip, c: { x: number; y: number }, s: number, aci: number): void {
    const W = (x: number, y: number) => this.duzlem2Dunya({ x, y });
    const seg = (x1: number, y1: number, x2: number, y2: number) => pts.push(W(x1, y1), W(x2, y2));
    const dir = { x: Math.cos(aci), y: Math.sin(aci) }, nrm = { x: -Math.sin(aci), y: Math.cos(aci) };
    // çizgi yönüne hizalı yerel eksende segment koy
    const L = (ld: number, ln: number) => ({ x: c.x + dir.x * ld + nrm.x * ln, y: c.y + dir.y * ld + nrm.y * ln });
    const ls = (a: { x: number; y: number }, b: { x: number; y: number }) => pts.push(W(a.x, a.y), W(b.x, b.y));
    switch (tip) {
      case 'parallel':       // "//" iki eğik tire
        ls(L(-s, -s), L(s, -s * 0.2)); ls(L(-s, s * 0.2), L(s, s));
        break;
      case 'perpendicular':  // "⊥"
        ls(L(0, -s), L(0, s)); ls(L(0, -s), L(s, -s));
        break;
      case 'horizontal':     // "—"
        ls(L(-s, 0), L(s, 0));
        break;
      case 'vertical':       // "|"
        ls(L(0, -s), L(0, s));
        break;
      case 'tangent':        // "T"
        ls(L(-s, s), L(s, s)); ls(L(0, s), L(0, -s));
        break;
      case 'equal':          // "=" iki yatay tire
        ls(L(-s, -s * 0.4), L(s, -s * 0.4)); ls(L(-s, s * 0.4), L(s, s * 0.4));
        break;
      case 'coincidence':    // küçük baklava (◇)
        ls(L(0, s), L(s, 0)); ls(L(s, 0), L(0, -s)); ls(L(0, -s), L(-s, 0)); ls(L(-s, 0), L(0, s));
        break;
      case 'concentric':     // iç içe iki "()"
        ls(L(-s, -s), L(-s, s)); ls(L(s, -s), L(s, s)); ls(L(-s * 0.4, -s * 0.6), L(-s * 0.4, s * 0.6)); ls(L(s * 0.4, -s * 0.6), L(s * 0.4, s * 0.6));
        break;
      case 'collinear':      // "—" çift çizgi
        ls(L(-s, -s * 0.3), L(s, -s * 0.3)); ls(L(-s, s * 0.3), L(s, s * 0.3));
        break;
      case 'fix':            // çapraz "✕" + çerçeve
        ls(L(-s, -s), L(s, s)); ls(L(-s, s), L(s, -s));
        break;
      case 'distance': case 'radius': case 'angle': // küçük kare (ölçü-tabanlı kısıt rozeti)
        ls(L(-s, -s), L(s, -s)); ls(L(s, -s), L(s, s)); ls(L(s, s), L(-s, s)); ls(L(-s, s), L(-s, -s));
        break;
      case 'symmetry':       // "↔" çift ok
        ls(L(-s, 0), L(s, 0)); ls(L(-s, 0), L(-s * 0.4, s * 0.5)); ls(L(-s, 0), L(-s * 0.4, -s * 0.5));
        ls(L(s, 0), L(s * 0.4, s * 0.5)); ls(L(s, 0), L(s * 0.4, -s * 0.5));
        break;
      case 'middlePoint':    // "├┤" üç dik tire
        ls(L(-s, -s * 0.6), L(-s, s * 0.6)); ls(L(0, -s), L(0, s)); ls(L(s, -s * 0.6), L(s, s * 0.6));
        break;
      case 'equidistant':    // "≡" üç yatay tire
        ls(L(-s, -s * 0.5), L(s, -s * 0.5)); ls(L(-s, 0), L(s, 0)); ls(L(-s, s * 0.5), L(s, s * 0.5));
        break;
    }
  }

  /** Sketch tanım durumu özeti (DOF feedback — SolidWorks "Fully Defined" rozeti için). */
  durumOzeti(): { eleman: number; olcu: number; serbest: number; tamTanimli: boolean; dof?: number; durum?: 'serbest' | 'tam' | 'asiri' } {
    const uretim = this.elemanlar.filter(e => !e.yapi);
    let serbest = 0;
    for (const el of uretim) if (this.elemanKisit(el) === 'serbest') serbest++;
    // Kalıcı kısıt katmanı varsa gerçek DOF'u kullan (CATIA "Fully Defined" rozeti)
    if (this.kisitSolver.kisitlar.length) {
      const d = this.kisitSolver.dof();
      return { eleman: uretim.length, olcu: this.olculer.length, serbest, tamTanimli: uretim.length > 0 && d.serbestlik <= 0, dof: d.serbestlik, durum: d.durum };
    }
    return { eleman: uretim.length, olcu: this.olculer.length, serbest, tamTanimli: uretim.length > 0 && serbest === 0, durum: 'serbest' };
  }

  /** Sketch çözüm durum rengi (CATIA Solving Status): under=mevcut, iso=mor, over=kırmızı. */
  private _durumRenkCache: 'serbest' | 'tam' | 'asiri' | null = null;
  cozumDurumu(): 'serbest' | 'tam' | 'asiri' {
    if (!this.kisitSolver.kisitlar.length) return 'serbest';
    return this.kisitSolver.dof().durum;
  }

  get secili(): ISketchEleman | null {
    return this.elemanlar.find(e => e.id === this.seciliId) || null;
  }

  /** Tüm uç/karakteristik noktalar (snap için) — dünya uzayında. */
  snapNoktalari(): THREE.Vector3[] {
    const out: THREE.Vector3[] = [];
    for (const el of this.elemanlar) {
      if (el.tip === 'cizgi') { out.push(this.duzlem2Dunya(el.noktalar[0]), this.duzlem2Dunya(el.noktalar[1])); }
      else if (el.tip === 'dikdortgen') {
        const [a, b] = el.noktalar;
        for (const n of [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }]) out.push(this.duzlem2Dunya(n));
      } else if (el.tip === 'cember') { out.push(this.duzlem2Dunya(el.noktalar[0])); }
      else if (el.tip === 'yay') {
        out.push(this.duzlem2Dunya(el.noktalar[0]), this.duzlem2Dunya(el.noktalar[1]));
        const c = FikoSketch.cevreCember(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
        if (c) out.push(this.duzlem2Dunya(c.merkez));
      } else if (el.tip === 'nokta') { out.push(this.duzlem2Dunya(el.noktalar[0])); }
    }
    return out;
  }

  /** 3 noktadan geçen çemberin merkezi+yarıçapı (kollinearsa null). */
  static cevreCember(a: ISketchNokta, b: ISketchNokta, c: ISketchNokta): { merkez: ISketchNokta; r: number } | null {
    const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
    if (Math.abs(d) < 1e-9) return null;
    const a2 = a.x * a.x + a.y * a.y, b2 = b.x * b.x + b.y * b.y, c2 = c.x * c.x + c.y * c.y;
    const ux = (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d;
    const uy = (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d;
    const merkez = { x: ux, y: uy };
    return { merkez, r: Math.hypot(a.x - ux, a.y - uy) };
  }

  /** 3 noktalı yay (start, end, üstünde nokta) → polyline noktaları (düzlem). */
  static yayNoktalari(p1: ISketchNokta, p2: ISketchNokta, p3: ISketchNokta, seg = 48): ISketchNokta[] {
    const c = FikoSketch.cevreCember(p1, p2, p3);
    if (!c) return [p1, p2]; // kollinear → düz çizgi
    const aci = (p: ISketchNokta) => Math.atan2(p.y - c.merkez.y, p.x - c.merkez.x);
    let a0 = aci(p1), a1 = aci(p2); const am = aci(p3);
    const norm = (x: number) => { while (x < 0) x += Math.PI * 2; while (x >= Math.PI * 2) x -= Math.PI * 2; return x; };
    a0 = norm(a0); a1 = norm(a1); const m = norm(am);
    // a0→a1 CCW yönünde am var mı?
    let ccw = true;
    const ccwIcerir = (s: number, e: number, t: number) => { const span = norm(e - s); const tt = norm(t - s); return tt <= span; };
    if (!ccwIcerir(a0, a1, m)) ccw = false;
    let span = ccw ? norm(a1 - a0) : -norm(a0 - a1);
    const out: ISketchNokta[] = [];
    for (let i = 0; i <= seg; i++) {
      const t = a0 + span * (i / seg);
      out.push({ x: c.merkez.x + Math.cos(t) * c.r, y: c.merkez.y + Math.sin(t) * c.r });
    }
    return out;
  }

  // ---- render ----

  ciz(): void {
    // temizle
    for (let i = this.nesne.children.length - 1; i >= 0; i--) {
      const c = this.nesne.children[i] as any;
      c.geometry?.dispose?.();
      this.nesne.remove(c);
    }
    const durumRenk = this.cozumDurumu();   // CATIA Solving Status: serbest/tam/asiri (sketch-geneli)
    for (const el of this.elemanlar) {
      const geo = this.elemanGeometri(el);
      if (!geo) continue;
      let mat: THREE.Material;
      if (el.id === this.seciliId) mat = this.malzemeSecili;
      else if (el.id === this.hoverId) mat = this.malzemeHover;
      else if (el.yapi) mat = this.malzemeYapi;
      else if (durumRenk === 'asiri') mat = this.malzemeAsiri;   // over → kırmızı
      else if (durumRenk === 'tam') mat = this.malzemeTam;       // iso → mor
      else { const k = this.elemanKisit(el); mat = (k === 'yatay' || k === 'dikey') ? this.malzemeKisitli : this.malzeme; }  // under: kısıtlı→yeşil, serbest→mavi
      const kapali = el.tip === 'dikdortgen' || el.tip === 'cember' || el.tip === 'cokgen' || el.tip === 'slot' || el.tip === 'elips' || (el.tip === 'spline' && !!el.kapali);
      const cizgi = kapali ? new THREE.LineLoop(geo, mat) : new THREE.Line(geo, mat);
      if (el.yapi && el.id !== this.seciliId && el.id !== this.hoverId) cizgi.computeLineDistances(); // kesik çizgi
      cizgi.userData['elemanId'] = el.id;   // mirror/offset/seçim için
      this.nesne.add(cizgi);
    }
    this.olculeriSenkron();
    if (this.olcuGoster) { this.olculeriCiz(); this.kisitlariCiz(); }  // sadece aktif sketch'te göster
  }

  private elemanGeometri(el: ISketchEleman): THREE.BufferGeometry | null {
    const pts: THREE.Vector3[] = [];
    if (el.tip === 'cizgi') {
      pts.push(this.duzlem2Dunya(el.noktalar[0]), this.duzlem2Dunya(el.noktalar[1]));
    } else if (el.tip === 'dikdortgen') {
      const [a, b] = el.noktalar;
      pts.push(
        this.duzlem2Dunya({ x: a.x, y: a.y }), this.duzlem2Dunya({ x: b.x, y: a.y }),
        this.duzlem2Dunya({ x: b.x, y: b.y }), this.duzlem2Dunya({ x: a.x, y: b.y }),
      );
    } else if (el.tip === 'cember') {
      const m = el.noktalar[0]; const r = el.r || 0;
      for (let i = 0; i < CEMBER_SEGMENT; i++) {
        const t = (i / CEMBER_SEGMENT) * Math.PI * 2;
        pts.push(this.duzlem2Dunya({ x: m.x + Math.cos(t) * r, y: m.y + Math.sin(t) * r }));
      }
    } else if (el.tip === 'yay') {
      const yp = FikoSketch.yayNoktalari(el.noktalar[0], el.noktalar[1], el.noktalar[2]);
      for (const p of yp) pts.push(this.duzlem2Dunya(p));
    } else if (el.tip === 'cokgen') {
      for (const p of FikoSketch.cokgenNoktalari(el)) pts.push(this.duzlem2Dunya(p));
    } else if (el.tip === 'slot') {
      for (const p of FikoSketch.slotNoktalari(el)) pts.push(this.duzlem2Dunya(p));
    } else if (el.tip === 'elips') {
      for (const p of FikoSketch.elipsNoktalari(el)) pts.push(this.duzlem2Dunya(p));
    } else if (el.tip === 'spline') {
      for (const p of FikoSketch.splineNoktalari(el)) pts.push(this.duzlem2Dunya(p));
    } else if (el.tip === 'konik') {
      for (const p of FikoSketch.konikNoktalari(el)) pts.push(this.duzlem2Dunya(p));
    } else return null;
    return new THREE.BufferGeometry().setFromPoints(pts);
  }

  /** KONİK: parabol (x=a·t²/2, y=b·t) veya hiperbol (x=a·cosh t, y=b·sinh t) — tepe/merkez+a+b+dönme. AÇIK eğri. */
  static konikNoktalari(el: ISketchEleman, seg = 72): ISketchNokta[] {
    const m = el.noktalar[0]; const a = el.r || 1, b = el.r2 || a; const rot = el.aci || 0;
    const cs = Math.cos(rot), sn = Math.sin(rot);
    const t0 = el.konikT0 ?? -2, t1 = el.konikT1 ?? 2; const out: ISketchNokta[] = [];
    for (let i = 0; i <= seg; i++) {
      const t = t0 + (t1 - t0) * (i / seg);
      let lx: number, ly: number;
      if (el.konikTip === 'hiperbol') { lx = a * Math.cosh(t); ly = b * Math.sinh(t); }
      else { ly = b * t; lx = (a * t * t) / 2; }
      out.push({ x: m.x + lx * cs - ly * sn, y: m.y + lx * sn + ly * cs });
    }
    return out;
  }

  /** PARAMETRİK elips: merkez + büyük yarıçap a (r) + küçük yarıçap b (r2) + dönme açısı. */
  static elipsNoktalari(el: ISketchEleman, seg = 72): ISketchNokta[] {
    const m = el.noktalar[0]; const a = el.r || 1, b = el.r2 || (el.r || 1) * 0.6; const rot = el.aci || 0;
    const cs = Math.cos(rot), sn = Math.sin(rot); const out: ISketchNokta[] = [];
    for (let i = 0; i < seg; i++) { const t = (i / seg) * Math.PI * 2; const ex = Math.cos(t) * a, ey = Math.sin(t) * b; out.push({ x: m.x + ex * cs - ey * sn, y: m.y + ex * sn + ey * cs }); }
    return out;
  }

  /** SPLINE (Catmull-Rom): kontrol noktalarından pürüzsüz eğri. kapali ise kapatır. */
  static splineNoktalari(el: ISketchEleman, segPer = 16): ISketchNokta[] {
    const cp = el.noktalar; if (cp.length < 2) return cp.slice();
    if (cp.length === 2) return cp.slice();
    const kapali = !!el.kapali; const out: ISketchNokta[] = [];
    const n = cp.length; const son = kapali ? n : n - 1;
    const P = (i: number) => cp[((i % n) + n) % n];
    for (let i = 0; i < son; i++) {
      const a = kapali ? P(i - 1) : cp[Math.max(0, i - 1)], b1 = cp[i], c1 = kapali ? P(i + 1) : cp[Math.min(n - 1, i + 1)], d = kapali ? P(i + 2) : cp[Math.min(n - 1, i + 2)];
      for (let j = 0; j < segPer; j++) {
        const t = j / segPer, t2 = t * t, t3 = t2 * t;
        const cr = (q0: number, q1: number, q2: number, q3: number) => 0.5 * ((2 * q1) + (-q0 + q2) * t + (2 * q0 - 5 * q1 + 4 * q2 - q3) * t2 + (-q0 + 3 * q1 - 3 * q2 + q3) * t3);
        out.push({ x: cr(a.x, b1.x, c1.x, d.x), y: cr(a.y, b1.y, c1.y, d.y) });
      }
    }
    if (!kapali) out.push(cp[n - 1]);
    return out;
  }

  /** PARAMETRİK çokgen köşeleri (merkez + yarıçap + kenarSayısı + başlangıç açısı). */
  static cokgenNoktalari(el: ISketchEleman): ISketchNokta[] {
    const m = el.noktalar[0]; const r = el.r || 0; const n = Math.max(3, Math.round(el.kenarSayisi || 6));
    const a0 = el.noktalar[1] ? Math.atan2(el.noktalar[1].y - m.y, el.noktalar[1].x - m.x) : -Math.PI / 2;
    const out: ISketchNokta[] = [];
    for (let i = 0; i < n; i++) { const t = a0 + (i / n) * Math.PI * 2; out.push({ x: m.x + Math.cos(t) * r, y: m.y + Math.sin(t) * r }); }
    return out;
  }

  /** PARAMETRİK slot (oval) konturu: iki merkez + yarıçap → 2 yarım daire + 2 düz kenar. */
  static slotNoktalari(el: ISketchEleman, seg = 16): ISketchNokta[] {
    const [c1, c2] = el.noktalar; const r = el.r || 0;
    const ang = Math.atan2(c2.y - c1.y, c2.x - c1.x);
    const dik = ang + Math.PI / 2;
    const out: ISketchNokta[] = [];
    for (let i = 0; i <= seg; i++) { const t = dik + (i / seg) * Math.PI; out.push({ x: c2.x + Math.cos(t) * r, y: c2.y + Math.sin(t) * r }); }   // c2 yarım daire
    for (let i = 0; i <= seg; i++) { const t = dik - Math.PI + (i / seg) * Math.PI; out.push({ x: c1.x + Math.cos(t) * r, y: c1.y + Math.sin(t) * r }); }  // c1 yarım daire
    return out;
  }

  // ════════════════ CATIA OPERATIONS (corner/chamfer/mirror/offset/trim) ════════════════

  private elemanBul(id: number): ISketchEleman | undefined { return this.elemanlar.find(e => e.id === id); }
  /** İki sonsuz çizginin kesişimi (paralel ise null). */
  private static cizgiKesisim(a1: ISketchNokta, a2: ISketchNokta, b1: ISketchNokta, b2: ISketchNokta): ISketchNokta | null {
    const d1x = a2.x - a1.x, d1y = a2.y - a1.y, d2x = b2.x - b1.x, d2y = b2.y - b1.y;
    const det = d1x * d2y - d1y * d2x; if (Math.abs(det) < 1e-9) return null;
    const t = ((b1.x - a1.x) * d2y - (b1.y - a1.y) * d2x) / det;
    return { x: a1.x + d1x * t, y: a1.y + d1y * t };
  }

  /** CORNER (köşe yuvarlatma): iki çizgi arasına r yarıçaplı teğet yay; çizgiler teğet noktasına kırpılır. */
  kose(id1: number, id2: number, r: number): ISketchEleman | null {
    const e1 = this.elemanBul(id1), e2 = this.elemanBul(id2);
    if (!e1 || !e2 || e1.tip !== 'cizgi' || e2.tip !== 'cizgi' || r <= 0) return null;
    const C = FikoSketch.cizgiKesisim(e1.noktalar[0], e1.noktalar[1], e2.noktalar[0], e2.noktalar[1]); if (!C) return null;
    // her çizginin C'den UZAK ucu → yön (C'den dışarı)
    const uzakUc = (e: ISketchEleman) => { const d0 = Math.hypot(e.noktalar[0].x - C.x, e.noktalar[0].y - C.y), d1 = Math.hypot(e.noktalar[1].x - C.x, e.noktalar[1].y - C.y); return d0 >= d1 ? 0 : 1; };
    const u1 = uzakUc(e1), u2 = uzakUc(e2);
    const dir = (p: ISketchNokta) => { const dx = p.x - C.x, dy = p.y - C.y; const l = Math.hypot(dx, dy) || 1; return { x: dx / l, y: dy / l }; };
    const d1 = dir(e1.noktalar[u1]), d2 = dir(e2.noktalar[u2]);
    const cosT = Math.max(-0.999, Math.min(0.999, d1.x * d2.x + d1.y * d2.y)); const yariAci = Math.acos(cosT) / 2;
    const dt = r / Math.tan(yariAci);                 // C'den teğet noktası mesafesi
    const t1 = { x: C.x + d1.x * dt, y: C.y + d1.y * dt }, t2 = { x: C.x + d2.x * dt, y: C.y + d2.y * dt };
    let bx = d1.x + d2.x, by = d1.y + d2.y; const bl = Math.hypot(bx, by) || 1; bx /= bl; by /= bl;
    const M = { x: C.x + bx * (r / Math.sin(yariAci)), y: C.y + by * (r / Math.sin(yariAci)) };  // yay merkezi
    const chordM = { x: (t1.x + t2.x) / 2, y: (t1.y + t2.y) / 2 };
    let mx = chordM.x - M.x, my = chordM.y - M.y; const ml = Math.hypot(mx, my) || 1; mx /= ml; my /= ml;
    const arcMid = { x: M.x + mx * r, y: M.y + my * r };   // yay orta noktası (köşeye bakan)
    // çizgileri teğet noktasına kırp (C ucu → teğet)
    e1.noktalar[1 - u1] = t1; e2.noktalar[1 - u2] = t2;
    const yay = this.ekle('yay', [t1, t2, arcMid]);   // ekle ciz() çağırır
    return yay;
  }

  /** CHAMFER (pah): iki çizgi arasına mesafe kadar düz pah; çizgiler kırpılır. */
  pah(id1: number, id2: number, mesafe: number): ISketchEleman | null {
    const e1 = this.elemanBul(id1), e2 = this.elemanBul(id2);
    if (!e1 || !e2 || e1.tip !== 'cizgi' || e2.tip !== 'cizgi' || mesafe <= 0) return null;
    const C = FikoSketch.cizgiKesisim(e1.noktalar[0], e1.noktalar[1], e2.noktalar[0], e2.noktalar[1]); if (!C) return null;
    const uzakUc = (e: ISketchEleman) => { const d0 = Math.hypot(e.noktalar[0].x - C.x, e.noktalar[0].y - C.y), d1 = Math.hypot(e.noktalar[1].x - C.x, e.noktalar[1].y - C.y); return d0 >= d1 ? 0 : 1; };
    const u1 = uzakUc(e1), u2 = uzakUc(e2);
    const dir = (p: ISketchNokta) => { const dx = p.x - C.x, dy = p.y - C.y; const l = Math.hypot(dx, dy) || 1; return { x: dx / l, y: dy / l }; };
    const d1 = dir(e1.noktalar[u1]), d2 = dir(e2.noktalar[u2]);
    const p1 = { x: C.x + d1.x * mesafe, y: C.y + d1.y * mesafe }, p2 = { x: C.x + d2.x * mesafe, y: C.y + d2.y * mesafe };
    e1.noktalar[1 - u1] = p1; e2.noktalar[1 - u2] = p2;
    return this.ekle('cizgi', [p1, p2]);
  }

  /** MIRROR (sketch içi aynalama): elemanları bir eksen (construction çizgi veya 2 nokta) etrafında yansıt. */
  aynalaSketch(ids: number[], eksenA: ISketchNokta, eksenB: ISketchNokta): ISketchEleman[] {
    const ax = eksenB.x - eksenA.x, ay = eksenB.y - eksenA.y; const al = Math.hypot(ax, ay) || 1; const ux = ax / al, uy = ay / al;
    const yansit = (p: ISketchNokta): ISketchNokta => { const vx = p.x - eksenA.x, vy = p.y - eksenA.y; const d = vx * ux + vy * uy; const px = eksenA.x + ux * d, py = eksenA.y + uy * d; return { x: 2 * px - p.x, y: 2 * py - p.y }; };
    const yeni: ISketchEleman[] = [];
    for (const id of ids) {
      const e = this.elemanBul(id); if (!e) continue;
      const nk = e.noktalar.map(yansit);
      const el: ISketchEleman = { id: this.sayac++, tip: e.tip, noktalar: nk, r: e.r, r2: e.r2, aci: e.aci ? -e.aci : undefined, kapali: e.kapali, kenarSayisi: e.kenarSayisi, yapi: e.yapi };
      this.elemanlar.push(el); yeni.push(el);
    }
    this.ciz();
    return yeni;
  }

  /** OFFSET (öteleme): çizgi/çember elemanını mesafe kadar dik öteler (yeni eleman). */
  otele(id: number, mesafe: number): ISketchEleman | null {
    const e = this.elemanBul(id); if (!e) return null;
    if (e.tip === 'cizgi') {
      const [a, b] = e.noktalar; const dx = b.x - a.x, dy = b.y - a.y; const l = Math.hypot(dx, dy) || 1; const nx = -dy / l, ny = dx / l;
      return this.ekle('cizgi', [{ x: a.x + nx * mesafe, y: a.y + ny * mesafe }, { x: b.x + nx * mesafe, y: b.y + ny * mesafe }]);
    }
    if (e.tip === 'cember') return this.ekle('cember', [{ ...e.noktalar[0] }], Math.max(0.1, (e.r || 0) + mesafe));
    if (e.tip === 'cokgen') { const o = this.ekle('cokgen', e.noktalar.map(p => ({ ...p })), Math.max(0.1, (e.r || 0) + mesafe)); o.kenarSayisi = e.kenarSayisi; this.ciz(); return o; }
    return null;
  }

  // ---- PROFİL ŞABLONLARI (CATIA Predefined Profile) — mevcut çizgi/çember/yay ile, yeni tip yok ----

  /** Yönlü dikdörtgen: merkez + en + boy + dönme açısı → 4 çizgi. */
  sablonYonluDikdortgen(merkez: ISketchNokta, en: number, boy: number, aci = 0): ISketchEleman[] {
    const cs = Math.cos(aci), sn = Math.sin(aci);
    const L = (dx: number, dy: number): ISketchNokta => ({ x: merkez.x + dx * cs - dy * sn, y: merkez.y + dx * sn + dy * cs });
    const k = [L(-en / 2, -boy / 2), L(en / 2, -boy / 2), L(en / 2, boy / 2), L(-en / 2, boy / 2)];
    const out: ISketchEleman[] = [];
    for (let i = 0; i < 4; i++) out.push(this.ekle('cizgi', [k[i], k[(i + 1) % 4]]));
    return out;
  }

  /** Paralelkenar: köşe + iki kenar (uzunluk+açı) → 4 çizgi. */
  sablonParalelkenar(kose: ISketchNokta, en: number, yan: number, aci1 = 0, aci2 = Math.PI / 3): ISketchEleman[] {
    const A = kose;
    const B = { x: A.x + Math.cos(aci1) * en, y: A.y + Math.sin(aci1) * en };
    const D = { x: A.x + Math.cos(aci2) * yan, y: A.y + Math.sin(aci2) * yan };
    const C = { x: B.x + (D.x - A.x), y: B.y + (D.y - A.y) };
    const k = [A, B, C, D]; const out: ISketchEleman[] = [];
    for (let i = 0; i < 4; i++) out.push(this.ekle('cizgi', [k[i], k[(i + 1) % 4]]));
    return out;
  }

  /** Anahtar deliği (keyhole): üst tam daire + alta uzanan dar yuva (2 çizgi + alt yay). */
  sablonAnahtarDeligi(daireMerkez: ISketchNokta, R: number, slotBoy: number, w: number): ISketchEleman[] {
    const out: ISketchEleman[] = [];
    out.push(this.ekle('cember', [{ ...daireMerkez }], R));
    const alt = { x: daireMerkez.x, y: daireMerkez.y - slotBoy };
    out.push(this.ekle('cizgi', [{ x: daireMerkez.x - w, y: daireMerkez.y }, { x: alt.x - w, y: alt.y }]));
    out.push(this.ekle('cizgi', [{ x: daireMerkez.x + w, y: daireMerkez.y }, { x: alt.x + w, y: alt.y }]));
    out.push(this.ekle('yay', [{ x: alt.x - w, y: alt.y }, { x: alt.x + w, y: alt.y }, { x: alt.x, y: alt.y - w }]));
    return out;
  }

  // ---- kalıcılık ----

  serialize(): any {
    return {
      v: 1,
      duzlem: {
        origin: this.duzlem.origin.toArray(), normal: this.duzlem.normal.toArray(),
        eksenU: this.duzlem.eksenU.toArray(), eksenV: this.duzlem.eksenV.toArray(),
      },
      elemanlar: this.elemanlar,
      sayac: this.sayac,
      olculer: this.olculer,
      olcuSayac: this.olcuSayac,
      otomatikOlcu: this.otomatikOlcu,
      kisitlar: this.kisitSolver.serialize(),   // kalıcı geometrik kısıt katmanı
    };
  }

  deserialize(o: any): void {
    if (!o) return;
    if (o.duzlem) {
      this.duzlem = {
        origin: new THREE.Vector3().fromArray(o.duzlem.origin),
        normal: new THREE.Vector3().fromArray(o.duzlem.normal),
        eksenU: new THREE.Vector3().fromArray(o.duzlem.eksenU),
        eksenV: new THREE.Vector3().fromArray(o.duzlem.eksenV),
      };
    }
    this.elemanlar = o.elemanlar || [];
    this.sayac = o.sayac || (this.elemanlar.length + 1);
    this.olculer = o.olculer || [];
    this.olcuSayac = o.olcuSayac || (this.olculer.length + 1);
    if (o.otomatikOlcu != null) this.otomatikOlcu = o.otomatikOlcu;
    if (o.kisitlar) this.kisitSolver.deserialize(o.kisitlar);   // kalıcı kısıtları geri yükle
    this.ciz();
  }

  dispose(): void {
    this.temizle();
    this.malzeme.dispose();
    this.malzemeSecili.dispose();
    this.malzemeHover.dispose();
    this.malzemeYapi.dispose();
    this.malzemeOlcu.dispose();
    this.malzemeOlcuSecili.dispose();
    this.malzemeKisitli.dispose();
    this.malzemeGlyph.dispose();
    this.malzemeKisitGlyph.dispose();
    this.malzemeTam.dispose();
    this.malzemeAsiri.dispose();
    for (const v of this.olcuTexCache.values()) { v.tex.dispose(); v.mat.dispose(); }
    this.olcuTexCache.clear();
  }
}
